import { FileText, ImageOff, Loader2 } from "lucide-react";
import * as React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/misc";
import { useMessageMedia } from "@/lib/queries";
import type { TextMedia } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The pictures a text carried (ADR-140), under its bubble.
 *
 * Each is fetched from the conversation's media route with the session's token (`useMessageMedia`)
 * — the route is scoped, so a plain `<img src>` would arrive without one. A thumbnail keeps a
 * fixed box whether or not its bytes have arrived: the thread sticks to the bottom by measuring
 * its own height (`PagedThread`), and an image that resized itself on load would pull the thread
 * out from under whoever is reading it.
 */

/** Why a picture is not there, in words a rep can act on. */
const WHY: Record<string, string> = {
  type: "This kind of file is not shown here",
  too_large: "This file was too large to keep",
  provider: "The phone network no longer has this file",
  no_answer: "The phone network did not send this file",
  no_store: "Pictures are not set up on this system",
};

const BOX = "size-28";

export function MessageMediaGrid({
  conversationId,
  media,
  align,
}: {
  /** Null on the development sample, where there is no route to ask. */
  conversationId: string | null;
  media: readonly TextMedia[];
  align: "left" | "right";
}) {
  const [open, setOpen] = React.useState<TextMedia | null>(null);
  if (media.length === 0) return null;
  return (
    <>
      <ul className={cn("flex flex-wrap gap-1.5", align === "right" && "justify-end")}>
        {media.map((item) => (
          <li key={item.id}>
            <MediaThumbnail
              conversationId={conversationId}
              item={item}
              onOpen={() => setOpen(item)}
            />
          </li>
        ))}
      </ul>
      <Dialog open={open !== null} onOpenChange={(next) => !next && setOpen(null)}>
        <DialogContent className="max-w-3xl">
          {open && <MediaFull conversationId={conversationId} item={open} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function MediaThumbnail({
  conversationId,
  item,
  onOpen,
}: {
  conversationId: string | null;
  item: TextMedia;
  onOpen: () => void;
}) {
  const showable = item.state === "stored" && item.kind === "image";
  const url = useMessageMedia(conversationId, item.id, showable);

  if (item.state === "failed") {
    return (
      <Tooltip content={WHY[item.error ?? ""] ?? "This file could not be kept"}>
        <span
          className={cn(
            BOX,
            "grid place-items-center rounded-lg border border-dashed border-border bg-surface-sunken text-text-subtle",
          )}
          role="img"
          aria-label={WHY[item.error ?? ""] ?? "This file could not be kept"}
        >
          <ImageOff className="size-5" aria-hidden />
        </span>
      </Tooltip>
    );
  }
  if (item.state === "pending") {
    return (
      <span
        className={cn(BOX, "grid place-items-center rounded-lg border border-border bg-surface-sunken")}
        role="status"
        aria-label="This picture is still arriving"
      >
        <Loader2 className="size-5 animate-spin text-text-subtle" aria-hidden />
      </span>
    );
  }
  if (item.kind === "file") {
    return (
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          BOX,
          "flex flex-col items-center justify-center gap-1 rounded-lg border border-border bg-surface px-2 text-2xs text-text-muted transition-colors hover:bg-surface-hover",
        )}
      >
        <FileText className="size-5" aria-hidden />
        <span className="truncate">Document</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        BOX,
        "overflow-hidden rounded-lg border border-border bg-surface-sunken transition-opacity hover:opacity-90",
      )}
      aria-label="Open this picture"
    >
      {url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : (
        <span className="grid size-full place-items-center">
          <Loader2 className="size-5 animate-spin text-text-subtle" aria-hidden />
        </span>
      )}
    </button>
  );
}

function MediaFull({
  conversationId,
  item,
}: {
  conversationId: string | null;
  item: TextMedia;
}) {
  const url = useMessageMedia(conversationId, item.id);
  if (!url) {
    return (
      <div className="grid min-h-48 place-items-center" role="status">
        <Loader2 className="size-6 animate-spin text-text-subtle" aria-hidden />
        <span className="sr-only">Opening</span>
      </div>
    );
  }
  if (item.kind === "image") {
    return <img src={url} alt="" className="max-h-[75vh] w-full object-contain" />;
  }
  // A PDF in its own frame: the browser's viewer, with no download link, since the sandboxed
  // viewer a rep sees is the same bytes the route served.
  return <iframe src={url} title="Document" className="h-[75vh] w-full rounded-md border border-border" />;
}
